import { Injectable } from '@angular/core';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { DataService } from './data.service';
import { LeasingService } from './leasing.service';
import { LinkPersonJsonData } from '../models/TenantLink';
import { FirmaElectronicaService } from './firma-electronica.service';
import { AuthUserResponse } from '../models/AuthUser';
import { TransaccionBaseByDocumentoResponse } from '../models/TransaccionBaseByDocumento';
import { CreateProcessRequest, ResponseCreateProcess } from '../models/CreateProcess';
import { CreateSignioTransactionModel, SignioCrearResponse } from '../models/CreateSignioTransaction';
import { CargarDocumentoRequest, CargarDocumentoResponse } from '../models/CargarDocumento';
import { RegistrarContactoRequest, RegistrarContactoResponse } from '../models/RegistrarContacto';
import { FirmaElctronicaResponse, VincularRequest } from '../models/Vincular';
import { DtoUpdateQuoteDocument } from '../models/DtoUpdateQuoteDocument';
import { ResponseBase } from '../models/ResponseBase';
import { ResponseSignioCrear } from '../models/firma/CreateSignioTransactionModel';
import { ApiResponseValidateForm } from '../../core/models/leasing/ApiResponseValidateForm';
import { ValidateForm } from '../../core/models/leasing/ValidateForm';

@Injectable({
  providedIn: 'root'
})
export class BusinessLogicService {
  constructor(
    private dataService: DataService,
    private leasingService: LeasingService,
    private firmaElectronicaService: FirmaElectronicaService,
  ) { }

  async loadAndProcessLinkQuoteData(quotationNumber: number): Promise<void> {
    try {
      const [arrendatarioLink, propietarioLink] = await Promise.all([
        firstValueFrom(this.leasingService.FindLinkQuotePerson(quotationNumber, 1)),
        firstValueFrom(this.leasingService.FindLinkQuotePerson(quotationNumber, 2))
      ]);

      if (arrendatarioLink?.value?.strJson) {
        try {
          const jsonData: LinkPersonJsonData = JSON.parse(arrendatarioLink.value.strJson);
          if (jsonData.f0) {
            this.dataService.updateTenantQuoteData(1, {
              roleId: 1,
              strEmail_v: jsonData.f0.strEmail_v,
              strCelular_v: jsonData.f0.strCelular_v,
              rolprincipal: jsonData.f0.rolprincipal,
              vincu: jsonData.f0.vincu,
              vinculos: jsonData.f0.vinculos,
              vinculado: jsonData.f0.vinculado
            });
          }
        } catch (error) {
          console.error('Error parsing arrendatario JSON:', error);
        }
      }

      if (propietarioLink?.value?.strJson) {
        try {
          const jsonData: LinkPersonJsonData = JSON.parse(propietarioLink.value.strJson);
          if (jsonData.f0) {
            this.dataService.updateTenantQuoteData(2, {
              roleId: 2,
              strEmail_v: jsonData.f0.strEmail_v,
              strCelular_v: jsonData.f0.strCelular_v,
              rolprincipal: jsonData.f0.rolprincipal,
              vincu: jsonData.f0.vincu,
              vinculos: jsonData.f0.vinculos
            });
          }
        } catch (error) {
          console.error('Error parsing propietario JSON:', error);
        }
      }
    } catch (error) {
      console.error('Error loading link data:', error);
      throw error;
    }
  }

  private async processElectronicSignature(role: number, quotationNumber: number): Promise<void> {
    return new Promise((resolve, reject) => {
    const quoteData = this.dataService.getQuoteData(); 
    const contacts = this.dataService.getQuoteContactData(); 
    const contact = contacts!.find(contact => contact.inIdRole === role);
    let strRoleName = '';

    switch (role) {
      case 1:
        strRoleName = 'Arrendatario';
        break;
      case 2:
        strRoleName = 'Propietario';
        break;
      case 3:
        strRoleName = 'Codeudor1';
        break;
      case 4:
        strRoleName = 'Codeudor2';
        break;
      case 5:
        strRoleName = 'Codeudor3';
        break;
      default:
        strRoleName = 'Rol desconocido';
        break;
    }

    // Paso 1 - GetTokenFirmaElectronica:
    this.firmaElectronicaService.GetTokenFirmaElectronica().subscribe({
      next: async (tokenResponse: AuthUserResponse) => {
        if (tokenResponse.status == true && tokenResponse.statusCode == 200) {
          const token = tokenResponse.result.token;
          // Paso 2 - GetTransaccionBaseByDocumento:
          this.firmaElectronicaService.GetTransaccionBaseByDocumento(token, 1).subscribe({
            next: async (responseTransaccionBase: TransaccionBaseByDocumentoResponse) => {
              if (responseTransaccionBase.status == true && responseTransaccionBase.statusCode == 200) {
                const transacciones_base: string[] = responseTransaccionBase.result.transaccion_Base;
                const proceso: CreateProcessRequest = this.buildCreateProcessRequest(quoteData!.inCodSucursal, quotationNumber, contact!.strContactEmail);
                // Paso 3 - CrearProceso:
                this.firmaElectronicaService.CrearProceso(token, proceso).subscribe({
                  next: async (responseCreateProcess: ResponseCreateProcess) => {
                    if (responseCreateProcess.status == true && responseCreateProcess.statusCode == 200) {
                      const signioTransaction: CreateSignioTransactionModel = this.buidSignioTransactionModel(quotationNumber, strRoleName, quoteData!.inCodSucursal, quoteData!.strContactEmail,
                        contact!.strContactFirstName, contact!.strContactFirstLastName, contact!.strContactSecondName, contact!.strContactSecondLastName,responseCreateProcess);
                      signioTransaction.signioCrear.id_transaccion_base = transacciones_base[0];
                      // Paso 4 - Crear:
                      this.firmaElectronicaService.Crear(token, signioTransaction).subscribe({
                        next: async (responseCrearSignio: SignioCrearResponse) => {
                          if (responseCrearSignio.status == true && responseCrearSignio.statusCode == 200) {
                            const id_transaccion = responseCrearSignio.result.signioTransaccion.id_transaccion;
                            // Paso  5 - GenerateFuccDocument:
                            this.leasingService.GenerateFuccDocument(quotationNumber, role).subscribe({
                              next: async (responseGenerateDocument: ResponseBase<any>) => {
                                if (responseGenerateDocument.status == true && responseGenerateDocument.value != null) {
                                  const document = responseGenerateDocument.value;
                                  const cargarDocumento: CargarDocumentoRequest = this.buildCargarDocumento(responseCrearSignio, document, strRoleName);
                                  // Paso 6 - CargarDocumento:
                                  this.firmaElectronicaService.CargarDocumento(token, cargarDocumento).subscribe({
                                    next: async (responseCargarDocumento: CargarDocumentoResponse) => {
                                      if (responseCargarDocumento.status == true && responseCargarDocumento.statusCode == 200) {
                                        const id_documento = responseCargarDocumento.result.signioResponse.id_documento;
                                        const registrarContacto: RegistrarContactoRequest = this.buildRegistrarContacto(id_transaccion, contact!.inContactIdentification, contact!.strContactEmail, 
                                          contact!.strContactMobileTelephoneNumber, contact!.strContactFirstName, contact!.strContactFirstLastName, contact!.strContactSecondName, contact!.strContactSecondLastName)
                                        // Paso 7 - RegistrarContacto:
                                        this.firmaElectronicaService.RegistrarContacto(token, registrarContacto).subscribe({
                                          next: async (responseRegistrarContacto: RegistrarContactoResponse) => {
                                            if (responseRegistrarContacto.status == true && responseRegistrarContacto.statusCode == 200) {
                                              const id_firmante = responseRegistrarContacto.result.signioResponse.id_firmante;
                                              const vincular: VincularRequest = this.buildVincular(id_transaccion, id_firmante, id_documento, strRoleName);
                                              // Paso 8 - Vincular:
                                              this.firmaElectronicaService.Vincular(token, vincular).subscribe({
                                                next: async (responseVincular: FirmaElctronicaResponse) => {
                                                  if (responseVincular.status == true && responseVincular.statusCode == 200) {
                                                    // Paso 9 - Distribuir:
                                                    this.firmaElectronicaService.Distribuir(token, id_transaccion).subscribe({
                                                      next: async (responseDistribuir: FirmaElctronicaResponse) => {
                                                        if (responseDistribuir.status == true && responseDistribuir.statusCode == 200) {
                                                          const quoteDocument: DtoUpdateQuoteDocument = this.buildDtoUpdateQuoteDocument(quotationNumber, responseCreateProcess.result.id_proceso, role);
                                                          // Paso 10 - UpdateProcessQuoteDocument:
                                                          this.firmaElectronicaService.UpdateProcessQuoteDocument(quoteDocument).subscribe({
                                                            next: async (responseUpdateProcessQuoteDocument: DtoUpdateQuoteDocument) => {
                                                              resolve();
                                                            },
                                                            error: (err) => {
                                                              console.error('Error UpdateProcessQuoteDocument:', err);
                                                              reject(err);
                                                            }
                                                          });
                                                        } else {
                                                          reject(new Error('Service Error'));
                                                        }
                                                      },
                                                      error: (err) => {
                                                        console.error('Error Distribuir:', err);
                                                        reject(err);
                                                      }
                                                    });
                                                  } else {
                                                    reject(new Error('Service Error'));
                                                  }
                                                },
                                                error: (err) => {
                                                  console.error('Error Vincular:', err);
                                                  reject(err);
                                                }
                                              });
                                            } else {
                                              reject(new Error('Service Error'));
                                            }
                                          },
                                          error: (err) => {
                                            console.error('Error RegistrarContacto:', err);
                                            reject(err);
                                          }
                                        });
                                      } else {
                                        reject(new Error('Service Error'));
                                      }
                                    },
                                    error: (err) => {
                                      console.error('Error CargarDocumento:', err);
                                      reject(err);
                                    }
                                  });
                                } else {
                                  reject(new Error('Service Error'));
                                }
                              },
                              error: (err) => {
                                console.error('Error GenerateFuccDocument:', err);
                                reject(err);
                              }
                            });
                          } else {
                            reject(new Error('Service Error'));
                          }
                        },
                        error: (err) => {
                          console.error('Error Crear:', err);
                          reject(err);
                        }
                      });
                    } else {
                      reject(new Error('Service Error'));
                    }
                  },
                  error: (err) => {
                    console.error('Error CrearProceso:', err);
                    reject(err);
                  }
                });
              } else {
                reject(new Error('Service Error'));
              }
            },
            error: (err) => {
              console.error('Error GetTransaccionBaseByDocumento:', err);
              reject(err);
            }
          });
        }
        else {
          reject(new Error('Service Error'));
        }
      },
      error: (err) => {
        console.error('Error GetTokenFirmaElectronica:', err);
        reject(err);
      }
    });
  });
}

  buildCreateProcessRequest(inCodSucursal: number, quotationNumber:number, email: string) {
    const proceso: CreateProcessRequest = {
      id_aplication: 26,
      id_tipo_proceso: 1,
      id_proceso_aplicacion: quotationNumber.toString(),
      cod_suc: inCodSucursal,
      cod_ramo: 45, // Todo Ver de donde Optengo Ramo desde Arrendamiento Web
      usuario: email, // Todo Ver si el  Usuario es el correcto
    };
    return proceso
  }

  buidSignioTransactionModel(quotationNumber:number, strRoleName: string, inCodSucursal: number, strContactEmail: string,
    strContactFirstName: string, strContactFirstLastName: string, strContactSecondName: string, strContactSecondLastName: string, process: ResponseCreateProcess) {

    const name = quotationNumber + '_' + strRoleName + '_' + strContactFirstName + strContactFirstLastName + strContactSecondName + strContactSecondLastName

    const transaction: CreateSignioTransactionModel = {
      signioCrear: {
        nombre: name,
        mensaje: '',
        ciclo_aprobacion: 0,
        tags: ['ts-' + inCodSucursal],
        id_transaccion_base: '',// TODO: Analizar de donde sale transacion base y actualizarla  por la Parametrizada
        external_id: null,
        cc: strContactEmail,
        op_btnRechazo: 0,
        op_foto: 'NO',
        op_certLegops: 1,
        op_firmasProcesadas: 1,
        remitente: '',
        mensaje_firma: '',
        mensaje_final: '',
        permiteEdicion: 1,
        permiteEdicionFirmantes: 1,
        campos_plantillas: {
          name: "",
          identification: ""
        },
        created_by: 'Solidaria'
      },
      camposPlantillas: {
        name: "",
        identification: ""
      },
      ApiFirmaElectronicaCrear: {
        id_proceso: process.result.id_proceso,
        id_aplication: 26,
        sn_aplica_transaccion_base: false,
        sn_elimina_suspendido: true,
      }
    }
    return transaction
  }

  buildCargarDocumento(transaccion: ResponseSignioCrear, documento: string, strRoleName: string) {
    const cargarDocumento: CargarDocumentoRequest = {
      Id_documento: 1, // TODO: Modificar esta logica para cuando se balla a implementar mas tipos de documentos
      Documento: {
        id_transaccion: transaccion.result.signioTransaccion.id_transaccion,
        nombreDocumento: `Formulario solicitud ${strRoleName}.pdf`,
        documento: documento,
        metatags: '',
        roles: strRoleName
      }
    }
    return cargarDocumento;
  }

  buildRegistrarContacto(id_transaccion: string, inContactIdentification: string, strContactEmail: string, strContactMobileTelephoneNumber: string,
    strContactFirstName: string, strContactFirstLastName: string, strContactSecondName: string, strContactSecondLastName: string) {
    const nombreContacto = strContactFirstName + ' ' + strContactFirstLastName + ' ' + strContactSecondName + ' ' + strContactSecondLastName

    const registrarContaco: RegistrarContactoRequest = {
      id_tipo_doc: 1,
      registrarContacto: {
        id_transaccion: id_transaccion,
        Nombre: nombreContacto,
        tipo_identificacion: 'CC',//TODO: Modificar este valor por el de BD aqui trea otro tido de formato (number)
        identificacion: inContactIdentification,
        email: strContactEmail,
        orden: 1, // TODO: Validar este Orden deacuerdo a los Firmantes
        aprobador: false,
        tipo_firma: 0,
        tipo_firmante: 1,
        celular: strContactMobileTelephoneNumber,
        razonsocial: '',
        company_identification_type: '',
        company_identification: '',
        servicio_identidad: '',
        states: [] // TODO: Validar estos Estados
      }
    }
    return registrarContaco;
  }

  buildVincular(transaccion:string, firmante:string, documento:string, strRoleName: string) {
    const vincular: VincularRequest = {
      id_transaccion: transaccion,
      id_firmante: firmante,
      id_documento: documento,
      posicion: null,
      attachment: false,
      stamps: null,
      operation_rol: strRoleName
    }
    return vincular;
  }

  buildDtoUpdateQuoteDocument(quotationNumber:number, id_proceso:number, inIdRole:number,) {
    const quoteDocument: DtoUpdateQuoteDocument = {
      InIdDocumentType: 21, // TODO : Verificar de donde sacar este valor porque no tiene sentido
      InIdQuoteHeader: quotationNumber,
      InIdRole: inIdRole,
      InIdProcesoFirma: id_proceso
    }
    return quoteDocument;
  }

  async sendSign(role: number) {
    try {
      const quotationNumber = this.dataService.getQuotationNumber();
      const response = await firstValueFrom(this.leasingService.getValidateFormsByQuotationNumber(quotationNumber!));
      const idSignatureProcess = response.value.find(sign => sign.idRole == role && sign.stateValidateForms == true)?.id;

      if(idSignatureProcess != null){
        await this.processElectronicSignature(role, quotationNumber!);
        const validateForm: ValidateForm = {
          id: idSignatureProcess,
          idRole: 0,
          quotationNumber: 0,
          idPerson: 0,
          dateValidateForms: new Date().toISOString(),  
          stateValidateForms: true,
          isEmailSent: true
        };
  
        await firstValueFrom(this.leasingService.createValidateForm(validateForm));
      }
      else{
        throw new Error('No idSignatureProcess found');
      }
    } catch (error) {
      console.error('Error en el proceso de firma electrónica:', error);
      throw error;
    }
  }

}